import { noddiAsync, URLKeys } from 'noddi-async';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import { useStationaryGarages } from './useStationaryGarages';

// Stationary bookings may live on an org UG linked to the car, not the personal session UG.
//
// Known limitation: this resolves the org UG from the user's *first* car. It powers the
// Home page, where no specific car/booking is in context, so a single representative car is
// used. Users whose cars span multiple org user groups only get the first car's org resolved;
// the rest fall back to the personal UG. Callers with a concrete car/booking in scope should
// resolve the org UG from that context instead of relying on this heuristic.
export function useBookingUserGroupId(userGroupId: number) {
  const { currentBrand } = useCurrentBrand();
  const {
    serviceOrganizationIds,
    isLoading: isGaragesLoading,
    error: stationaryGaragesError
  } = useStationaryGarages({ withCapacity: false });

  const hasStationaryOrgs = !isGaragesLoading && serviceOrganizationIds.length > 0;

  const {
    data: carsData,
    isPending: isCarsPending,
    error: carsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      userGroupIds: [userGroupId],
      pageSize: 1,
      pageIndex: 0,
      brandDomains: [currentBrand]
    },
    queryConfig: { enabled: hasStationaryOrgs }
  });

  const firstCarPlate = carsData?.results[0]?.licensePlate.number;

  const {
    data: orgsForCar,
    isLoading: isOrgsLoading,
    error: orgsError
  } = noddiAsync.useGet({
    type: URLKeys.getOrganizationsForCar,
    input: {
      licensePlateNumber: firstCarPlate!,
      serviceOrganizationIds,
      userGroupId
    },
    queryConfig: { enabled: !!firstCarPlate && serviceOrganizationIds.length > 0 }
  });

  const bookingUserGroupId = orgsForCar?.[0]?.userGroupId ?? userGroupId;

  // Ready when org lookup is unnecessary, finished, or failed (fall back to personal UG).
  const isReady =
    !isGaragesLoading &&
    (!hasStationaryOrgs ||
      Boolean(carsError) ||
      Boolean(orgsError) ||
      (!isCarsPending && carsData !== undefined && (!firstCarPlate || orgsForCar !== undefined)));

  return {
    bookingUserGroupId,
    isReady,
    isLoading: !isReady || isOrgsLoading,
    error: stationaryGaragesError ?? carsError ?? orgsError ?? null
  };
}
